import { MapPin, Star, Wifi, Lock } from "lucide-react";
import FavoriteButton from "@/components/property/FavoriteButton";

/**
 * PropertyCard — komponen siap-pakai untuk menampilkan ringkasan hunian.
 * Menerima prop `property`. Jika kosong, menampilkan skeleton placeholder.
 * `compact` = versi ringkas untuk layar kecil (foto/nama/alamat mengecil, ala Traveloka),
 * tampilan desktop tetap normal.
 * `locked` = properti owner dengan subscription tidak aktif (blur + gembok, tidak bisa dibuka).
 */
export default function PropertyCard({ property, index = 0, propertyId, favorited = false, onToggleFavorite, compact = false, locked = false }) {
  const p = property || {};
  const isPlaceholder = !property;

  return (
    <div
      className={`group bg-card border border-border rounded-2xl overflow-hidden transition-transform ${locked ? "opacity-90" : "hover:-translate-y-1 hover:shadow-lg"}`}
      data-testid={`property-card-${index}`}
      data-locked={locked ? "true" : "false"}
    >
      {/* Foto */}
      <div
        className={`relative bg-secondary/60 overflow-hidden ${compact ? "aspect-[3/2] sm:aspect-[4/3]" : "aspect-[4/3]"}`}
        data-testid={`property-card-photo-${index}`}
      >
        {p.photo ? (
          <img src={p.photo} alt={p.name} className={`w-full h-full object-cover transition-transform duration-300 ${locked ? "blur-md scale-105" : "group-hover:scale-105"}`} />
        ) : (
          <div className={`w-full h-full grid place-items-center text-xs text-muted-foreground ${locked ? "blur-sm" : ""}`}>
            Foto hunian
          </div>
        )}
        {locked && (
          <div className="absolute inset-0 grid place-items-center bg-black/45" data-testid={`property-card-locked-${index}`}>
            <div className="flex flex-col items-center text-white">
              <Lock size={26} />
              <span className="mt-1 text-xs font-bold uppercase tracking-wider">Tidak Aktif</span>
            </div>
          </div>
        )}
        <span className={`absolute top-2 left-2 sm:top-3 sm:left-3 font-bold uppercase tracking-wider rounded-full bg-primary text-primary-foreground ${compact ? "text-[9px] sm:text-[10px] px-2 py-0.5 sm:px-2.5 sm:py-1" : "text-[10px] px-2.5 py-1"}`}>
          {p.type || "Kos"}
        </span>
        {propertyId && !locked && (
          <div className="absolute top-2 right-2 sm:top-3 sm:right-3">
            <FavoriteButton
              propertyId={propertyId}
              initialFavorited={favorited}
              onToggled={onToggleFavorite}
            />
          </div>
        )}
      </div>

      <div className={compact ? "p-3 sm:p-5" : "p-5"}>
        {/* Nama */}
        <h3
          className={`font-heading font-semibold truncate ${compact ? "text-sm sm:text-lg" : "text-lg"}`}
          data-testid={`property-card-name-${index}`}
        >
          {p.name || (isPlaceholder ? "Nama hunian" : "")}
        </h3>

        {/* Lokasi */}
        <p
          className={`mt-1 flex items-center gap-1 sm:gap-1.5 text-muted-foreground truncate ${compact ? "text-xs sm:text-sm" : "text-sm"}`}
          data-testid={`property-card-location-${index}`}
        >
          <MapPin size={compact ? 12 : 14} className="shrink-0" /> {p.location || (isPlaceholder ? "Lokasi hunian" : "")}
        </p>

        {/* Rating + review count */}
        <div
          className={`flex items-center gap-1.5 ${compact ? "mt-2 sm:mt-3 text-xs sm:text-sm" : "mt-3 text-sm"}`}
          data-testid={`property-card-rating-${index}`}
        >
          <Star size={compact ? 13 : 15} className="text-accent fill-accent shrink-0" />
          <span className="font-semibold text-foreground">{p.rating ?? "—"}</span>
          <span className="text-muted-foreground" data-testid={`property-card-reviews-${index}`}>
            ({p.reviewCount ?? 0} ulasan)
          </span>
        </div>

        {/* Fasilitas utama — disembunyikan pada versi compact di layar kecil */}
        <div
          className={`flex flex-wrap gap-1.5 ${compact ? "hidden sm:flex mt-3" : "mt-3"}`}
          data-testid={`property-card-amenities-${index}`}
        >
          {(p.amenities || ["WiFi", "AC", "K. Mandi Dalam"]).slice(0, 3).map((a) => (
            <span key={a} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-secondary/60 text-muted-foreground">
              <Wifi size={11} /> {a}
            </span>
          ))}
        </div>

        {/* Harga */}
        <div className={`flex items-end justify-between border-t border-border ${compact ? "mt-3 pt-3 sm:mt-4 sm:pt-4" : "mt-4 pt-4"}`}>
          <div data-testid={`property-card-price-${index}`}>
            <span className={`font-heading font-bold text-primary ${compact ? "text-base sm:text-xl" : "text-xl"}`}>{p.price || "Rp —"}</span>
            {p.price && p.price !== "—" && <span className="text-xs text-muted-foreground"> {p.priceSuffix || "/bln"}</span>}
          </div>
          <span className="text-xs font-semibold text-accent whitespace-nowrap">{locked ? "Subscription Owner belum aktif" : "Lihat detail →"}</span>
        </div>
      </div>
    </div>
  );
}
